import React, { useEffect } from "react";
import TextareaField from "@/components/form/TextAreaField";
import RadioButton from "@/components/ui/RadioButton";
import { updateUser } from "@/redux/feature/user/userSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";

type SpecFields = 
  | "noiselessRoomAndMicrophoneSpecs"
  | "cameraOrSmartphoneSpecs"
  | "lightingSpecs"
  | "additionalInfo";

interface BaseSpec {
  details: string;
}

interface AudioSpec extends BaseSpec {
  hasAudioEquipment: boolean | null;
}

interface CameraSpec extends BaseSpec {
  hasCamera: boolean | null;
}

interface LightingSpec extends BaseSpec {
  hasLighting: boolean | null;
}

interface AdditionalSpec extends BaseSpec {
  hasAdditionalExperience: boolean | null;
}

type SpecType = {
  noiselessRoomAndMicrophoneSpecs: AudioSpec;
  cameraOrSmartphoneSpecs: CameraSpec;
  lightingSpecs: LightingSpec;
  additionalInfo: AdditionalSpec;
}

type HasPropertyMap = {
  noiselessRoomAndMicrophoneSpecs: "hasAudioEquipment";
  cameraOrSmartphoneSpecs: "hasCamera";
  lightingSpecs: "hasLighting";
  additionalInfo: "hasAdditionalExperience";
}

interface ConditionalFieldProps {
  fieldName: SpecFields;
  question: string;
  textareaLabel: string;
  textareaPlaceholder: string;
  required?: boolean;
  height?: string;
  hasPropertyName: HasPropertyMap[SpecFields];
  data?: string;
}

const YesNoOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
] as const;

const EditConditionalField = ({
  fieldName,
  question,
  textareaLabel,
  textareaPlaceholder,
  required = false,
  height = "100px",
  hasPropertyName,
  data,
}: ConditionalFieldProps) => {
  const dispatch = useAppDispatch();
  const fieldData = useAppSelector((state) => state.user.user[fieldName]) as SpecType[SpecFields];

  // Type guard to check if a value is boolean
  const isBoolean = (value: any): value is boolean => 
    typeof value === 'boolean';

  useEffect(() => {
    if (data && data !== fieldData.details) {
      dispatch(
        updateUser({
          field: fieldName,
          value: {
            ...fieldData,
            [hasPropertyName]: true,
            details: data,
          },
        })
      );
    }
  }, [data, fieldData, dispatch, hasPropertyName, fieldName]);

  const handleOptionChange = (value: boolean) => {
    dispatch(
      updateUser({
        field: fieldName,
        value: {
          ...fieldData,
          [hasPropertyName]: value,
          details: value ? fieldData.details : "",
        },
      })
    );
  };

  const handleDetailsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      updateUser({
        field: fieldName,
        value: {
          ...fieldData,
          details: e.target.value,
        },
      })
    );
  };

  const getBooleanValue = () => {
    const value = fieldData[hasPropertyName as keyof typeof fieldData];
    return isBoolean(value) ? value : null;
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[14px] mb-3 text-[#344054] font-medium">
          {question}
        </p>
        <div className="flex gap-x-8 items-center">
          {YesNoOptions.map((item) => (
            <div key={item.title} className="gap-x-2 flex items-center">
              <RadioButton
                onChange={() => handleOptionChange(item.value)}
                checked={getBooleanValue() === item.value}
              />
              <span className="text-sm font-medium text-gray-500">
                {item.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      {getBooleanValue() === true && (
        <div>
          <TextareaField
            onChange={handleDetailsChange}
            name={fieldName}
            label={textareaLabel}
            placeholder={textareaPlaceholder}
            height={height}
            value={fieldData.details}
            error={
              required && !fieldData.details ? "This field is required" : ""
            }
          />
        </div>
      )}
    </div>
  );
};

export default EditConditionalField;